import { X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { IconField } from '@/components/admin/shared/icon-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { buildContainerTree, descendantIdsOf } from '@/lib/coaching/source-tree'
import { normalizeKind } from '@/lib/source-content'
import { useSourceContent, useSourceContentKinds } from './hooks/use-sources'
import { ACTION_VERB_CHIPS, suggestedActionVerb } from './lib'
import type { SourceContent } from './types'
import { SOURCE_CONTENT_TYPES } from './types'

interface ContentFormData {
  title: string
  description: string
  kind: string
  actionVerb: string
  order: number
  url: string
  estimatedMinutes: string
  icon: string
  parentId: string | null
}

const emptyForm: ContentFormData = {
  title: '',
  description: '',
  kind: 'chapter',
  actionVerb: '',
  order: 0,
  url: '',
  estimatedMinutes: '',
  icon: '',
  parentId: null,
}

interface SourceContentModalProps {
  open: boolean
  sourceId: string
  /** null = create mode */
  editingContent: SourceContent | null
  /** Pre-selected parent for create mode ("Add child" / "Add sub-container") */
  defaultParentId: string | null
  /** The source's live containers, for the parent picker (any order) */
  allContents: SourceContent[]
  onClose: () => void
}

export function SourceContentModal({
  open,
  sourceId,
  editingContent,
  defaultParentId,
  allContents,
  onClose,
}: SourceContentModalProps) {
  const { addContent, updateContent } = useSourceContent(sourceId)
  const { kinds } = useSourceContentKinds()

  const [form, setForm] = useState<ContentFormData>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)
  const [kindFocused, setKindFocused] = useState(false)
  // Whether the admin has typed in the Order field this open; while false the
  // end-of-sequence default below keeps self-correcting as data arrives.
  const [orderTouched, setOrderTouched] = useState(false)

  // Re-seed the form whenever the modal (re)opens for a different target.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reseed only on open/target change
  useEffect(() => {
    if (!open) return
    setOrderTouched(false)
    if (editingContent) {
      setForm({
        title: editingContent.title,
        description: editingContent.description || '',
        kind: editingContent.kind,
        actionVerb: editingContent.actionVerb || '',
        order: editingContent.order,
        url: editingContent.url || '',
        estimatedMinutes: editingContent.estimatedMinutes?.toString() || '',
        icon: editingContent.icon || '',
        parentId: editingContent.parentId ?? null,
      })
    } else {
      setForm({ ...emptyForm, parentId: defaultParentId })
    }
  }, [open, editingContent?.id, defaultParentId])

  // Create-mode default order: sibling containers plus the parent's items
  // share one order space, so the end-of-sequence default is their combined
  // count. Owned by this effect (not the reseed above) so it self-corrects
  // when `allContents` is still loading at open time or the Parent selection
  // changes — until the admin types an explicit order.
  useEffect(() => {
    if (!open || editingContent || orderTouched) return
    const siblings = allContents.filter(
      (c) => (c.parentId ?? null) === form.parentId,
    )
    const parent = form.parentId
      ? allContents.find((c) => c.id === form.parentId)
      : undefined
    const defaultOrder = siblings.length + (parent?.itemCount ?? 0)
    setForm((prev) =>
      prev.order === defaultOrder ? prev : { ...prev, order: defaultOrder },
    )
  }, [open, editingContent, orderTouched, allContents, form.parentId])

  // Parent options: the source tree flattened depth-first with indentation.
  // Editing: exclude self + descendants (server cycle guard is the backstop).
  const parentOptions = useMemo(() => {
    const excluded = editingContent
      ? new Set([
          editingContent.id,
          ...descendantIdsOf(allContents, editingContent.id),
        ])
      : new Set<string>()
    const tree = buildContainerTree(allContents)
    const options: { id: string; label: string }[] = []
    const walk = (parentId: string | null, depth: number) => {
      for (const node of tree.get(parentId) ?? []) {
        if (excluded.has(node.id)) continue
        options.push({
          id: node.id,
          label: `${'—'.repeat(depth)} ${node.title}`.trim(),
        })
        walk(node.id, depth + 1)
      }
    }
    walk(null, 0)
    return options
  }, [allContents, editingContent])

  // Kind suggestions: live distinct kinds merged with the legacy six, current
  // input as a prefix/substring filter (case-insensitive).
  const kindSuggestions = useMemo(() => {
    const seen = new Set<string>()
    const merged: string[] = []
    for (const k of kinds) {
      if (!seen.has(k.kind)) {
        seen.add(k.kind)
        merged.push(k.kind)
      }
    }
    for (const t of SOURCE_CONTENT_TYPES) {
      if (!seen.has(t.value)) {
        seen.add(t.value)
        merged.push(t.value)
      }
    }
    const query = form.kind.trim().toLowerCase()
    if (!query) return merged
    return merged.filter((k) => k.toLowerCase().includes(query))
  }, [kinds, form.kind])

  // Free-text row: offered when the normalized input isn't already an exact
  // suggestion, so typing a genuinely new kind has an explicit affordance
  // instead of relying on submitting the raw (un-normalized) text.
  const normalizedKindInput = normalizeKind(form.kind)
  const kindSuggestionsLower = useMemo(
    () => new Set(kindSuggestions.map((k) => k.toLowerCase())),
    [kindSuggestions],
  )
  const showFreeTextKindRow =
    normalizedKindInput.length > 0 &&
    !kindSuggestionsLower.has(normalizedKindInput)

  const verbPlaceholder =
    suggestedActionVerb(form.kind) ?? 'e.g., Read, Watch, Complete'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      toast.error('Title is required')
      return
    }
    if (!form.kind.trim()) {
      toast.error('Kind is required')
      return
    }

    setIsSaving(true)
    try {
      const data = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        kind: form.kind.trim(),
        actionVerb: form.actionVerb.trim() || null,
        order: form.order,
        url: form.url.trim() || null,
        parentId: form.parentId,
        estimatedMinutes: form.estimatedMinutes
          ? Number.parseInt(form.estimatedMinutes, 10)
          : null,
        icon: form.icon.trim() || null,
      }
      if (editingContent) {
        await updateContent(editingContent.id, data)
        toast.success('Content updated')
      } else {
        await addContent({ ...data, sourceId })
        toast.success('Content added')
      }
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save content')
    } finally {
      setIsSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">
            {editingContent ? 'Edit Content' : 'Add Content'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="content-title"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Title <span className="text-destructive">*</span>
            </label>
            <Input
              id="content-title"
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g., Chapter 1: Introduction"
              required
            />
          </div>

          <div>
            <label
              htmlFor="content-parent"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Parent
            </label>
            <select
              id="content-parent"
              value={form.parentId ?? ''}
              onChange={(e) => {
                // The default-order effect above retargets the end-of-sequence
                // default for the new scope (create mode, order untouched).
                const nextParentId = e.target.value || null
                setForm((prev) => ({ ...prev, parentId: nextParentId }))
              }}
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              <option value="">— Top level —</option>
              {parentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div
            className="relative"
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setKindFocused(false)
              }
            }}
          >
            <label
              htmlFor="content-kind"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Kind <span className="text-destructive">*</span>
            </label>
            <Input
              id="content-kind"
              type="text"
              autoComplete="off"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              onFocus={() => setKindFocused(true)}
              placeholder="e.g., chapter, section, quiz"
            />
            {kindFocused &&
              (kindSuggestions.length > 0 || showFreeTextKindRow) && (
                <div className="border-border bg-popover absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-md border shadow-md">
                  {showFreeTextKindRow && (
                    <button
                      key="__free-text__"
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm({ ...form, kind: normalizedKindInput })
                        setKindFocused(false)
                      }}
                      className="text-foreground hover:bg-muted block w-full px-3 py-1.5 text-left text-sm"
                    >
                      Use "{normalizedKindInput}"
                    </button>
                  )}
                  {kindSuggestions.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setForm({ ...form, kind: k })
                        setKindFocused(false)
                      }}
                      className="text-foreground hover:bg-muted block w-full px-3 py-1.5 text-left text-sm"
                    >
                      {k}
                    </button>
                  ))}
                </div>
              )}
            <p className="text-muted-foreground mt-1 text-xs">
              Free text — pick a suggestion or type a new kind (saved
              lowercase).
            </p>
          </div>

          <div>
            <label
              htmlFor="content-action-verb"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Action verb (optional)
            </label>
            <Input
              id="content-action-verb"
              type="text"
              value={form.actionVerb}
              onChange={(e) => setForm({ ...form, actionVerb: e.target.value })}
              placeholder={verbPlaceholder}
            />
            <div className="mt-1.5 flex gap-1.5">
              {ACTION_VERB_CHIPS.map((verb) => (
                <button
                  key={verb}
                  type="button"
                  onClick={() => setForm({ ...form, actionVerb: verb })}
                  className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
                    form.actionVerb === verb
                      ? 'border-primary text-primary'
                      : 'border-border text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {verb}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label
              htmlFor="content-description"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Description
            </label>
            <textarea
              id="content-description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={3}
              className="border-input bg-background w-full rounded-md border p-3 text-sm"
              placeholder="Optional description..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="content-order"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Order
              </label>
              <Input
                id="content-order"
                type="number"
                min="0"
                value={form.order}
                onChange={(e) => {
                  setOrderTouched(true)
                  setForm({
                    ...form,
                    order: Number.parseInt(e.target.value, 10) || 0,
                  })
                }}
              />
            </div>

            <div>
              <label
                htmlFor="content-minutes"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Est. Minutes
              </label>
              <Input
                id="content-minutes"
                type="number"
                min="0"
                value={form.estimatedMinutes}
                onChange={(e) =>
                  setForm({ ...form, estimatedMinutes: e.target.value })
                }
                placeholder="e.g., 30"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="content-url"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              URL
            </label>
            <Input
              id="content-url"
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div>
            <IconField
              label="Icon (optional)"
              value={form.icon}
              onChange={(icon) => setForm({ ...form, icon })}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : editingContent ? 'Update' : 'Add'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
